import { Component, Prop, Vue } from "vue-property-decorator";
import WithRender from "./ComplaintFrequencyPerSenderChart.html";
import Chart from "chart.js";
import { Inject } from "vue-di-container";
import Notifications330Service from "../Notifications330Service";
import { Notifications330FilterModel } from "../Notifications330FilterModel";
import { ComplaintFrequencyPerSenderReadModel } from "../Notifications330ReadModel";
import {
    buildMonitorDashboardBaseTooltipStyling,
    buildMonitorDashboardCategoryAxisTicks,
    buildMonitorDashboardLegendTop,
    buildMonitorDashboardResponsiveChartOptions,
    buildMonitorDashboardValueAxisTicks,
    createMonitorDashboardLegendHeightPlugin,
    getMonitorDashboardFontFamily,
    getMonitorDashboardChartColor,
    monitorDashboardDarkerBorder,
    monitorDashboardLegendClickHandler,
    MONITOR_CHART_GRID_COLOR,
} from "../../chart/monitorDashboardChartKit";
import { saveNotifications330FilterToStorage, NOTIFICATIONS330_DETAILS_ROUTES, buildNotifications330ScopePayload } from "../notifications330DetailsShared";

const ROW_HEIGHT_PX = 32;
const CHART_MIN_HEIGHT_PX = 280;
const CHART_VERTICAL_CHROME_PX = 120;

@WithRender
@Component({
    diProvide: [Notifications330Service],
})
export default class ComplaintFrequencyPerSenderChart extends Vue {
    @Inject(Notifications330Service)
    public notifications330Service!: Notifications330Service;

    @Prop({ required: true }) public filter!: Notifications330FilterModel;

    private chart: any = null;
    private canvasId: string = "complaint-frequency-per-sender-chart-canvas";

    public hasData: boolean = false;
    public isLoading: boolean = false;
    public totalCount: number = 0;
    private requestVersion: number = 0;
    private rowCount: number = 0;

    public get chartHostStyle(): Record<string, string> {
        const h = Math.max(
            CHART_MIN_HEIGHT_PX,
            this.rowCount * ROW_HEIGHT_PX + CHART_VERTICAL_CHROME_PX,
        );
        return {
            height: h + "px",
            width: "100%",
            minWidth: "0",
            maxWidth: "100%",
            position: "relative",
            boxSizing: "border-box",
        };
    }

    public onTitleClick(): void {
        this.navigateToDetails("");
    }

    private navigateToDetails(senderName: string): void {
        if (!this.filter) {
            return;
        }

        saveNotifications330FilterToStorage(this.filter, senderName || "");

        this.$router.push({ name: NOTIFICATIONS330_DETAILS_ROUTES.frequency });
    }

    public async refresh(): Promise<void> {
        if (!this.filter) return;

        this.isLoading = true;
        const currentRequestVersion = ++this.requestVersion;

        const payload = buildNotifications330ScopePayload(this.filter);

        const resp = await this.notifications330Service.GetComplaintsFrequencyPerSender(payload);
        if (currentRequestVersion !== this.requestVersion) {
            return;
        }

        this.isLoading = false;

        if (!resp || !resp.result || !Array.isArray(resp.result)) {
            this.hasData = false;
            this.totalCount = 0;
            this.rowCount = 0;
            this.destroyChart();
            return;
        }

        const rows = resp.result as ComplaintFrequencyPerSenderReadModel[];
        if (resp.totalCount !== undefined && resp.totalCount !== null) {
            this.totalCount = Number(resp.totalCount);
        } else {
            let total = 0;
            for (let i = 0; i < rows.length; i++) {
                total +=
                    (rows[i].deactivatedCount != null ? Number(rows[i].deactivatedCount) : 0) +
                    (rows[i].falseComplaintCount != null ? Number(rows[i].falseComplaintCount) : 0);
            }
            this.totalCount = total;
        }
        this.rowCount = rows.length;
        this.hasData = rows.length > 0;

        await this.$nextTick();
        if (!this.hasData) {
            this.destroyChart();
            return;
        }
        this.renderChart(rows);
    }

    public beforeDestroy(): void {
        this.destroyChart();
    }

    private destroyChart(): void {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    private renderChart(rows: ComplaintFrequencyPerSenderReadModel[]): void {
        const labels: string[] = [];
        const deactivated: number[] = [];
        const falseComplaint: number[] = [];

        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            labels.push(r.senderName ? String(r.senderName) : "");
            const d = r.deactivatedCount != null ? Number(r.deactivatedCount) : 0;
            const f = r.falseComplaintCount != null ? Number(r.falseComplaintCount) : 0;
            deactivated.push(isNaN(d) ? 0 : d);
            falseComplaint.push(isNaN(f) ? 0 : f);
        }

        this.destroyChart();

        const el = document.getElementById(this.canvasId) as HTMLCanvasElement;
        if (!el) return;

        const ctx = el.getContext("2d") as any;
        const self = this;
        const fontFamily = getMonitorDashboardFontFamily();
        const baseOpts = buildMonitorDashboardResponsiveChartOptions();
        const fillDe = getMonitorDashboardChartColor("complaintFrequencyPerSender", "2");
        const fillFa = getMonitorDashboardChartColor("complaintFrequencyPerSender", "3");
        const borderDe = monitorDashboardDarkerBorder(fillDe);
        const borderFa = monitorDashboardDarkerBorder(fillFa);

        const labelDe = String(this.$t("monitorDashboard.statusDeactivated"));
        const labelFa = String(this.$t("monitorDashboard.statusFalseComplaint"));
        const senderNameCaption = String(this.$t("monitorDashboard.complaintFrequencyTooltipSenderName"));
        const chartLabels = labels;
        const chartDeactivated = deactivated;
        const chartFalseComplaint = falseComplaint;

        let xMax = 0;
        for (let i = 0; i < rows.length; i++) {
            const sum = deactivated[i] + falseComplaint[i];
            if (sum > xMax) xMax = sum;
        }
        if (xMax <= 0) xMax = 10;
        const withPadding = Math.ceil(xMax * 1.08);
        const step = this.getNiceStep(Math.ceil(withPadding / 12));
        const xAxisMax = Math.ceil(withPadding / step) * step;

        this.chart = new Chart(ctx, {
            type: "horizontalBar",
            data: {
                labels: labels,
                datasets: [
                    {
                        label: labelDe,
                        data: deactivated,
                        backgroundColor: fillDe,
                        borderColor: borderDe,
                        borderWidth: 2,
                        stack: "freq",
                    },
                    {
                        label: labelFa,
                        data: falseComplaint,
                        backgroundColor: fillFa,
                        borderColor: borderFa,
                        borderWidth: 2,
                        stack: "freq",
                    },
                ],
            },
            plugins: [
                createMonitorDashboardLegendHeightPlugin(12),
                {
                    afterDatasetsDraw: function (chart: any) {
                        const ctx2 = chart.ctx;
                        ctx2.save();
                        ctx2.textAlign = "center";
                        ctx2.textBaseline = "middle";
                        ctx2.font = "12px " + fontFamily;
                        ctx2.fillStyle = "#FFFFFF";

                        const datasets =
                            chart.data && chart.data.datasets ? chart.data.datasets : [];
                        for (let di = 0; di < datasets.length; di++) {
                            const dataset = datasets[di];
                            const meta = chart.getDatasetMeta(di);
                            if (!meta || meta.hidden || !meta.data) {
                                continue;
                            }

                            for (let i = 0; i < meta.data.length; i++) {
                                const value = Number(dataset.data[i] || 0);
                                if (value <= 0) {
                                    continue;
                                }

                                const bar = meta.data[i];
                                const model = bar && bar._model ? bar._model : null;
                                if (!model) {
                                    continue;
                                }

                                const barWidth = Math.abs(Number(model.base) - Number(model.x));
                                if (barWidth < 18) {
                                    continue;
                                }

                                const leftX = Math.min(Number(model.base), Number(model.x));
                                const centerX = leftX + barWidth / 2;
                                ctx2.fillText(String(value), centerX, model.y);
                            }
                        }

                        ctx2.restore();
                    },
                },
            ],
            options: Object.assign({}, baseOpts, {
                layout: {
                    padding: { top: 8, right: 8, bottom: 8, left: 8 },
                },
                onClick: function (evt: any, elements: any[]) {
                    if (!elements || elements.length === 0) {
                        return;
                    }
                    const idx = elements[0]._index;
                    const d =
                        chartDeactivated[idx] != null ? Number(chartDeactivated[idx]) : 0;
                    const f =
                        chartFalseComplaint[idx] != null
                            ? Number(chartFalseComplaint[idx])
                            : 0;
                    const total = (isNaN(d) ? 0 : d) + (isNaN(f) ? 0 : f);
                    if (total <= 0) {
                        return;
                    }
                    const name =
                        chartLabels[idx] != null ? String(chartLabels[idx]) : "";
                    if (!name) {
                        return;
                    }
                    self.navigateToDetails(name);
                },
                legend: buildMonitorDashboardLegendTop(fontFamily, monitorDashboardLegendClickHandler),
                scales: {
                    xAxes: [
                        {
                            stacked: true,
                            gridLines: { color: MONITOR_CHART_GRID_COLOR },
                            ticks: buildMonitorDashboardValueAxisTicks(11, fontFamily, true, xAxisMax, undefined),
                        },
                    ],
                    yAxes: [
                        {
                            stacked: true,
                            gridLines: { display: false },
                            ticks: Object.assign(
                                {},
                                buildMonitorDashboardCategoryAxisTicks(11, fontFamily, 0),
                                {
                                    reverse: true,
                                    callback: function (_value: any, index: number) {
                                        const name =
                                            labels[index] != null ? String(labels[index]) : "";
                                        const d =
                                            deactivated[index] != null
                                                ? Number(deactivated[index])
                                                : 0;
                                        const f =
                                            falseComplaint[index] != null
                                                ? Number(falseComplaint[index])
                                                : 0;
                                        const total =
                                            (isNaN(d) ? 0 : d) + (isNaN(f) ? 0 : f);
                                        if (name.length === 0) {
                                            return "";
                                        }
                                        return name + " (" + String(total) + ")";
                                    },
                                },
                            ),
                        },
                    ],
                },
                tooltips: Object.assign({}, buildMonitorDashboardBaseTooltipStyling(), {
                    mode: "index",
                    intersect: false,
                    callbacks: {
                        title: function (items: any[]) {
                            if (!items || items.length === 0) return "";
                            const it = items[0];
                            const idx = it.index;
                            const name =
                                it.yLabel != null && it.yLabel !== undefined && String(it.yLabel).length > 0
                                    ? String(it.yLabel)
                                    : labels[idx];
                            return senderNameCaption + ": " + name;
                        },
                        label: function (tooltipItem: any, data: any) {
                            const ds =
                                data && data.datasets && data.datasets[tooltipItem.datasetIndex]
                                    ? data.datasets[tooltipItem.datasetIndex]
                                    : null;
                            const statusLabel = ds && ds.label ? String(ds.label) : "";
                            const raw =
                                tooltipItem.value !== undefined && tooltipItem.value !== null
                                    ? Number(tooltipItem.value)
                                    : Number(tooltipItem.xLabel);
                            const value = isNaN(raw) ? 0 : raw;
                            const statusCaption = String(self.$t("monitorDashboard.complaintFrequencyTooltipComplaintStatus"));
                            return statusCaption + ": " + statusLabel + " — " + String(value);
                        },
                    },
                }),
            }),
        });

        const chartRef = this.chart;
        this.$nextTick(function () {
            if (chartRef && chartRef.resize) {
                chartRef.resize();
            }
        });
    }

    private getNiceStep(rawStep: number): number {
        if (rawStep <= 1) return 1;
        const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
        const normalized = rawStep / magnitude;
        if (normalized <= 1) return 1 * magnitude;
        if (normalized <= 2) return 2 * magnitude;
        if (normalized <= 5) return 5 * magnitude;
        return 10 * magnitude;
    }
}
